import type { WorkItemCard } from './types';

/**
 * What actually changed on a task, as the body of a PATCH.
 *
 * WHY A DIFF AND NOT "SEND EVERYTHING". The drawer used to post every field on every save,
 * and once the permissions were split that broke twice over:
 *
 *   A customer fixing a typo sent `points` and `dueDate` along with the title — fields they
 *   may not set — and the API refused the whole save with a message about sizing work they
 *   had never touched.
 *
 *   `stage` was missing from the list altogether, so choosing a stage marked the drawer
 *   dirty, saved cleanly, and changed nothing. The one field somebody had just set was the
 *   one field never sent.
 *
 * A diff answers both: nothing untouched is mentioned, so no gate is tripped by a field
 * nobody is changing, and a field added to the drawer cannot be left out of the save by
 * omission — it is compared like every other.
 */
export function changedFields(loaded: WorkItemCard, next: WorkItemCard): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  if (next.title !== loaded.title) patch.title = next.title;
  if (next.type !== loaded.type) patch.type = next.type;
  if (next.priority !== loaded.priority) patch.priority = next.priority;
  if (next.description !== loaded.description) patch.description = next.description;
  if (next.reproSteps !== loaded.reproSteps) patch.reproSteps = next.reproSteps;
  if (next.stage !== loaded.stage) patch.stage = next.stage;
  if (next.points !== loaded.points) patch.points = next.points;
  if (next.blockedReason !== loaded.blockedReason) patch.blockedReason = next.blockedReason;

  /** Null is a VALUE here, not an omission: clearing a date is `null`, and `undefined` leaves it. */
  if (next.dueDate !== loaded.dueDate) patch.dueDate = next.dueDate;
  if (next.startDate !== loaded.startDate) patch.startDate = next.startDate;

  const names = next.assignees.map((person) => person.id);
  const before = loaded.assignees.map((person) => person.id);
  /* The whole set, so removing the last name is as expressible as adding one. */
  if (names.join() !== before.join()) patch.assigneeIds = names;

  return patch;
}
